import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Mail, RotateCcw, Send, Info, Eye, EyeOff, ChevronDown, ChevronUp, Code, Type } from 'lucide-react';
import { RichTextEditor } from '@/components/RichTextEditor';

const API = process.env.REACT_APP_BACKEND_URL;

export function EmailConfigPanel({ projectId }) {
  const [config, setConfig] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [htmlMode, setHtmlMode] = useState(false);

  const token = localStorage.getItem('pulse_token');

  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/projects/${projectId}/email-config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setConfig(data);
      setSubject(data.invitation_subject || '');
      setBody(data.invitation_body || '');
      if (data.is_custom) setShowCustomize(true);
    } catch {
      toast.error('Failed to load email configuration');
    } finally {
      setLoading(false);
    }
  }, [projectId, token]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/projects/${projectId}/email-config`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          invitation_subject: subject,
          invitation_body: body,
        }),
      });
      if (!res.ok) throw new Error();
      toast.success('Email template saved');
      fetchConfig();
    } catch {
      toast.error('Failed to save email configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      const res = await fetch(`${API}/api/projects/${projectId}/email-config`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success('Email template reset to Pulse default');
      setBody('');
      setShowCustomize(false);
      fetchConfig();
    } catch {
      toast.error('Failed to reset email configuration');
    }
  };

  const handleSendTest = async () => {
    setTesting(true);
    try {
      const res = await fetch(`${API}/api/projects/${projectId}/email-config/test`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to send');
      toast.success(data.message);
    } catch (err) {
      toast.error(err.message || 'Failed to send test email');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-slate-400">Loading email configuration...</CardContent>
      </Card>
    );
  }

  const isUsingDefault = !config?.is_custom;

  return (
    <div className="space-y-4" data-testid="email-config-panel">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-[#7FCDC1]" />
              <div>
                <CardTitle className="text-lg">Invitation Email Template</CardTitle>
                <CardDescription>
                  The branded Pulse template is sent when stakeholders are invited to the survey.
                </CardDescription>
              </div>
            </div>
            {isUsingDefault && (
              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-[#7FCDC1]/10 text-[#5AA2AE] border border-[#7FCDC1]/20" data-testid="default-template-badge">
                Default Template Active
              </span>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Subject Line */}
          <div className="space-y-2">
            <Label htmlFor="email-subject">Subject Line</Label>
            <Input
              id="email-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Welcome to Pulse - {{project}}"
              data-testid="email-config-subject"
            />
          </div>

          {/* Default Template Preview */}
          {isUsingDefault && (
            <div className="space-y-3">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="flex items-center gap-2 text-sm font-medium text-[#5AA2AE] hover:text-[#5AA2AE]/80 transition-colors"
                data-testid="toggle-preview-btn"
              >
                {showPreview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                {showPreview ? 'Hide' : 'Preview'} Default Template
              </button>
              {showPreview && (
                <div className="border rounded-lg overflow-hidden bg-[#f6f6f6]" data-testid="email-preview">
                  <div className="p-6 text-center">
                    <img src="/static/images/top_icon.png" alt="Pulse" className="w-14 h-14 mx-auto mb-4" />
                    <p className="text-sm text-slate-600 mb-1">
                      Welcome <strong>{'{{name}}'}</strong>,
                    </p>
                    <p className="text-sm text-slate-600 mb-4">
                      you have been nominated as a stakeholder on<br />
                      <strong>{'{{project}}'}</strong> by <strong>{'{{inviter}}'}</strong>.
                    </p>
                  </div>
                  <div className="bg-white mx-8 mb-4 rounded-md py-10 px-16 shadow-sm">
                    <div className="text-center mb-6">
                      <img src="/static/images/invitation.png" alt="Invitation" className="w-[40%] mx-auto" />
                    </div>
                    <p className="text-sm text-slate-600 text-center font-semibold mb-3">
                      You have been invited to share your perspective on the project using Pulse by ProjectAI
                    </p>
                    <p className="text-xs text-slate-500 text-center mb-3">
                      We want to create the kind of project where everyone can freely share their thoughts, feelings and ideas. We want this project to succeed - and to do that, we need your honest input.
                    </p>
                    <p className="text-xs text-slate-500 text-center mb-3">
                      We are using Pulse to anonymously gather the views of our stakeholders. This is more than a survey. There are no wrong answers and you can change your responses at any time.
                    </p>
                    <hr className="my-3 border-slate-200" />
                    <p className="text-xs text-slate-500 text-center mb-4">
                      Let me know if you have any questions about Pulse or if you get stuck, email{' '}
                      <span className="text-[#7FCDC1]">support@projectai.com</span>
                      <br />-So, let's get started.
                    </p>
                    <div className="text-center">
                      <span className="inline-block px-6 py-2.5 bg-[#7FCDC1] text-white text-sm rounded-full font-medium">Log In</span>
                    </div>
                  </div>
                  <div className="text-center pb-4">
                    <p className="text-[10px] text-slate-400">&copy; 2026 All Rights Reserved</p>
                    <p className="text-[10px] text-slate-500 font-semibold">Pulse by ProjectAI</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Customize Override Section */}
          <div className="border-t pt-4">
            <button
              onClick={() => setShowCustomize(!showCustomize)}
              className="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
              data-testid="toggle-customize-btn"
            >
              {showCustomize ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              {showCustomize ? 'Hide' : 'Show'} Custom Override
            </button>

            {showCustomize && (
              <div className="mt-3 space-y-4">
                <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-100 text-sm text-amber-700">
                  <Info className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    Setting a custom body replaces the default Pulse template. Available placeholders:{' '}
                    <code className="bg-amber-100 px-1 rounded text-xs font-mono">{'{{name}}'}</code>,{' '}
                    <code className="bg-amber-100 px-1 rounded text-xs font-mono">{'{{project}}'}</code>,{' '}
                    <code className="bg-amber-100 px-1 rounded text-xs font-mono">{'{{inviter}}'}</code>
                  </span>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Custom Email Body</Label>
                    <button
                      onClick={() => setHtmlMode(!htmlMode)}
                      className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md border transition-colors hover:bg-slate-50"
                      data-testid="toggle-html-mode-btn"
                    >
                      {htmlMode ? (
                        <><Type className="h-3.5 w-3.5" /> Rich Editor</>
                      ) : (
                        <><Code className="h-3.5 w-3.5" /> HTML Mode</>
                      )}
                    </button>
                  </div>

                  {htmlMode ? (
                    <Textarea
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      rows={12}
                      className="font-mono text-sm"
                      placeholder="<p>Hi {{name}},</p><p>You have been invited to {{project}} by {{inviter}}...</p>"
                      data-testid="email-config-body-html"
                    />
                  ) : (
                    <RichTextEditor
                      value={body}
                      onChange={setBody}
                      placeholder="Start typing your custom email body... Use {{name}}, {{project}}, {{inviter}} for dynamic content."
                    />
                  )}
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-[#5AA2AE] hover:bg-[#5AA2AE]/90"
                    data-testid="email-config-save-btn"
                  >
                    {saving ? 'Saving...' : 'Save Custom Template'}
                  </Button>
                  {config?.is_custom && (
                    <Button
                      variant="ghost"
                      onClick={handleReset}
                      className="text-slate-500"
                      data-testid="email-config-reset-btn"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Reset to Pulse Default
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="border-t pt-4 flex gap-3">
            <Button
              variant="outline"
              onClick={handleSendTest}
              disabled={testing}
              data-testid="email-config-test-btn"
            >
              <Send className="h-4 w-4 mr-2" />
              {testing ? 'Sending...' : 'Send Test Email'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
